import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AiAdminApiService, AiCompanySettings, AiRoleLimits, AiRoleKey } from '../../services/ai-admin-api.service';
import { StorageService } from '../../../../services/storage.service';

@Component({
    selector: 'app-ai-settings',
    templateUrl: './settings-page.component.html',
    styleUrls: ['./settings-page.component.scss']
})
export class AiSettingsPageComponent implements OnInit, OnDestroy {
    form: FormGroup;
    loading = true;
    saving = false;
    error: string | null = null;
    successMessage: string | null = null;
    settings: AiCompanySettings | null = null;

    private destroy$ = new Subject<void>();
    private companyId: string = '';

    constructor(
        private fb: FormBuilder,
        private aiAdminApi: AiAdminApiService,
        private storageService: StorageService,
    ) {
        this.form = this.fb.group({});
    }

    ngOnInit(): void {
        this.companyId = this.storageService.get('company')?.id || '';
        if (!this.companyId) {
            this.error = 'No se pudo obtener la información de la empresa';
            this.loading = false;
            return;
        }
        this.loadSettings();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadSettings(): void {
        this.loading = true;
        this.error = null;
        this.aiAdminApi.getSettings(this.companyId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (settings) => {
                    this.settings = settings;
                    this.buildForm(settings);
                    this.loading = false;
                },
                error: (err) => {
                    this.loading = false;
                    this.error = this._getErrorMessage(err);
                }
            });
    }

    /**
     * Construye el formulario dinámico con los límites por rol
     */
    private buildForm(settings: AiCompanySettings): void {
        const limitGroups: { [key: string]: FormGroup } = {};

        // Si no hay límites, inicializar con los roles por defecto
        const limitsToUse = settings.limits.length > 0
            ? settings.limits
            : this._getDefaultLimits();

        // Crear un grupo de formulario para cada rol
        limitsToUse.forEach(limit => {
            limitGroups[limit.roleKey] = this.fb.group({
                dailyTokenLimit: [
                    limit.dailyTokenLimit,
                    [Validators.min(1), Validators.pattern(/^\d+$/)]
                ],
                weeklyTokenLimit: [
                    limit.weeklyTokenLimit,
                    [Validators.min(1), Validators.pattern(/^\d+$/)]
                ],
                monthlyTokenLimit: [
                    limit.monthlyTokenLimit,
                    [Validators.min(1), Validators.pattern(/^\d+$/)]
                ],
            });
        });

        // Actualizar settings si estaban vacíos
        if (settings.limits.length === 0) {
            settings.limits = limitsToUse;
        }

        this.form = this.fb.group({
            limits: this.fb.group(limitGroups)
        });
    }

    /**
     * Retorna los límites por defecto si no existen
     */
    private _getDefaultLimits(): AiRoleLimits[] {
        return [
            {
                roleKey: 'company_learner',
                dailyTokenLimit: null,
                weeklyTokenLimit: null,
                monthlyTokenLimit: null,
                isEnabled: true,
            },
            // {
            //     roleKey: 'company_author',
            //     dailyTokenLimit: null,
            //     weeklyTokenLimit: null,
            //     monthlyTokenLimit: null,
            //     isEnabled: true,
            // }
        ];
    }

    /**
     * Obtiene el grupo de límites para un rol específico
     */
    getLimitGroup(roleKey: AiRoleKey): FormGroup {
        return (this.form.get('limits') as FormGroup).get(roleKey) as FormGroup;
    }

    /**
     * Obtiene la etiqueta legible para un rol
     */
    getRoleLabel(roleKey: AiRoleKey): string {
        const labels: Record<AiRoleKey, string> = {
            'company_learner': 'Aprendices (company_learner)',
            'company_author': 'Autores (company_author)',
        };
        return labels[roleKey];
    }

    /**
     * Obtiene la descripción para un rol
     */
    getRoleDescription(roleKey: AiRoleKey): string {
        const descriptions: Record<AiRoleKey, string> = {
            'company_learner': 'Usuarios con rol de aprendiz que pueden usar el asistente de IA',
            'company_author': 'Usuarios con rol de autor que pueden usar el asistente de IA para crear contenido',
        };
        return descriptions[roleKey];
    }

    /**
     * Obtiene la lista de roles configurables
     */
    getRoles(): AiRoleKey[] {
        if (!this.settings) return [];
        return this.settings.limits.map(l => l.roleKey) as AiRoleKey[];
    }

    /**
     * Guarda la configuración
     */
    save(): void {
        if (this.form.invalid || this.saving) {
            return;
        }

        this.error = null;
        this.successMessage = null;
        this.saving = true;

        // Preparar los datos para enviar
        const formData = this.form.value;
        const settingsToSave: AiCompanySettings = {
            isAiEnabled: true,
            limits: []
        };

        // Reconstruir el array de límites desde el formulario
        const rolesData = formData.limits as { [key: string]: any };
        Object.keys(rolesData).forEach(roleKey => {
            const roleData = rolesData[roleKey];
            const dailyLimit = roleData.dailyTokenLimit ? parseInt(roleData.dailyTokenLimit) : null;
            const weeklyLimit = roleData.weeklyTokenLimit ? parseInt(roleData.weeklyTokenLimit) : null;
            const monthlyLimit = roleData.monthlyTokenLimit ? parseInt(roleData.monthlyTokenLimit) : null;

            // Validar jerarquía de límites
            if (!this._validateLimitHierarchy(dailyLimit, weeklyLimit, monthlyLimit)) {
                this.error = 'Los límites deben cumplir: diario ≤ semanal ≤ mensual';
                this.saving = false;
                return;
            }

            settingsToSave.limits.push({
                roleKey: roleKey as AiRoleKey,
                isEnabled: true,
                dailyTokenLimit: dailyLimit,
                weeklyTokenLimit: weeklyLimit,
                monthlyTokenLimit: monthlyLimit,
            });
        });

        this.aiAdminApi.updateSettings(this.companyId, settingsToSave)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (updated) => {
                    this.settings = updated;
                    this.saving = false;
                    this.successMessage = 'Configuración guardada correctamente';
                    // Limpiar mensaje de éxito después de 3 segundos
                    setTimeout(() => {
                        this.successMessage = null;
                    }, 3000);
                },
                error: (err) => {
                    this.saving = false;
                    this.error = this._getErrorMessage(err);
                }
            });
    }

    /**
     * Valida la jerarquía de límites: daily <= weekly <= monthly
     */
    private _validateLimitHierarchy(daily: number | null, weekly: number | null, monthly: number | null): boolean {
        if (daily !== null && weekly !== null && daily > weekly) {
            return false;
        }
        if (weekly !== null && monthly !== null && weekly > monthly) {
            return false;
        }
        if (daily !== null && monthly !== null && daily > monthly) {
            return false;
        }
        return true;
    }

    /**
     * Obtiene mensaje de error amigable
     */
    private _getErrorMessage(err: any): string {
        if (err.status === 401) {
            return 'No tienes permiso para actualizar esta configuración';
        }
        if (err.status === 403) {
            return 'Solo el propietario de la empresa puede cambiar esta configuración';
        }
        if (err.status === 404) {
            return 'No se encontró la información de la empresa';
        }
        if (err.status === 422) {
            const errors = err.error?.errors;
            if (errors) {
                return Object.values(errors).flat().join(', ');
            }
            return err.error?.message || 'Datos inválidos. Verifica los límites';
        }
        if (err.error?.message) {
            return err.error.message;
        }
        return 'Error al guardar. Por favor, intenta de nuevo';
    }
}
